// Invoices (roadmap Phase 1) — the screens. Shown only when Settings › Features › Billing system
// is "This app", and only to admin/office staff. Every write goes through the Cloud Functions in
// functions/invoices.js (saveInvoice / issueInvoice / voidInvoice / deleteDraftInvoice), which
// recompute totals and stop the same operation being billed twice; this file only edits drafts
// and shows what the server saved.
//
//   initInvoices()                          wires the Work Orders toolbar "Invoices" button
//   openInvoicesList(tab)                   'invoices' (the list) or 'queue' (billed, not invoiced)
//   invoiceWorkOrder(workOrder, customerId) a draft from that work order's billed operations
//   invoiceStorageContract(row, part)      'deposit' or 'balance' invoice for a storage contract
//   openInvoiceEditor({ invoice } | { draft })

import { auth, database, functions } from './initFirebase';
import { ref, onValue, get, query, orderByChild, equalTo } from 'firebase/database';
import { deviceCopy } from './deviceCopy';
import { httpsCallable } from 'firebase/functions';
import { idbCursorAll, toastMessage, escapeHTML, woLabel, STORAGE_SERVICE_CATALOG, STORAGE_DEFAULTS, currentSeasonLabel } from './utilities';
import { toCents, toDollars, formatMoney, lineCents, sumCents, priceTotals, PRICING_DEFAULTS, STORAGE_RATE_DEFAULTS } from './money';
import { usesDockmaster } from './marinaConfig';
import { openAppInvoiceDocument } from './estimates';
import { openPaymentDialog, openPaymentDetail, renderPaymentsList, setPaymentsDeps } from './payments';
import { renderAccountsTab, openStatement } from './accounts';
import { termsLine } from './collectionsCore';

const esc = v => escapeHTML(v == null ? '' : String(v));
const call = (name, data) => httpsCallable(functions, name, { timeout: 60000 })(data).then(r => r.data);
const errText = e => String(e?.message || e).replace(/^FirebaseError:\s*/, '');
const roleTokens = r => String(r || '').split(',').map(x => x.trim().toLowerCase()).filter(Boolean);

/** Tell other screens (Storage Contracts' invoice buttons) that an invoice changed. */
const changed = () => window.dispatchEvent(new CustomEvent('invoices:changed'));

const STATUS = {
  draft: { label: 'Draft', cls: 'inv-pill-draft' },
  sent: { label: 'Open', cls: 'inv-pill-open' },
  partial: { label: 'Part paid', cls: 'inv-pill-partial' },
  paid: { label: 'Paid', cls: 'inv-pill-paid' },
  void: { label: 'Void', cls: 'inv-pill-void' },
};
const FILTERS = [['open', 'Open'], ['draft', 'Drafts'], ['paid', 'Paid'], ['void', 'Void'], ['all', 'All']];
const QUEUE_DAYS = 60;

const S = {
  invoices: {}, index: {}, rates: { ...PRICING_DEFAULTS },
  unsubs: [], filter: 'open', search: '', tab: 'invoices', since: null,
};

/* ============ who + setup ============ */

let canBill = false;
async function refreshAccess() {
  const u = auth.currentUser;
  if (!u) { canBill = false; return; }
  try {
    const roles = roleTokens((await get(ref(database, `users/${u.uid}/role`))).val());
    canBill = roles.includes('admin') || roles.includes('office');
  } catch { canBill = false; }
  document.querySelectorAll('.inv-needs-billing').forEach(el => el.classList.toggle('inv-hidden', !canBill));
}

export function initInvoices() {
  const btn = document.getElementById('invoices-list-button');
  if (!btn || btn.dataset.wired) return;
  btn.dataset.wired = '1';
  btn.addEventListener('click', () => openInvoicesList('invoices'));
  setPaymentsDeps({
    openInvoice: async id => {
      const inv = S.invoices[id] || (await get(ref(database, `invoices/${id}`))).val();
      if (inv) openInvoiceEditor({ invoice: inv });
    },
  });
  // Payments change invoices (and the Payments tab) without going through this file.
  window.addEventListener('invoices:changed', () => {
    if (document.getElementById('inv-list') && (S.tab === 'payments' || S.tab === 'accounts')) renderList();
  });
  auth.onAuthStateChanged(() => refreshAccess());
}

/** Live copies of /invoices, /invoiceIndex/jobs and the pricing rates while a screen is open. */
function watch() {
  if (S.unsubs.length) return;
  // From the device copy (deviceCopy.js), not a download of every invoice.
  const copy = deviceCopy('invoices');
  const load = () => copy.all().then(v => {
    S.invoices = v;
    if (document.getElementById('inv-list')) renderList();
  }).catch(e => console.warn('[invoices] list', e));
  load();
  let timer;
  S.unsubs.push(copy.onChange(() => { clearTimeout(timer); timer = setTimeout(load, 200); }));
  S.unsubs.push(onValue(ref(database, 'invoiceIndex/jobs'), snap => {
    S.index = snap.val() || {};
    if (document.getElementById('inv-list') && S.tab === 'queue') renderList();
  }, e => console.warn('[invoices] index', e)));
  get(ref(database, 'pricing/estimateDefaults')).then(s => { S.rates = { ...PRICING_DEFAULTS, ...(s.val() || {}) }; }).catch(() => {});
}
function unwatch() {
  S.unsubs.forEach(u => u());
  S.unsubs = [];
}

function guard() {
  if (usesDockmaster()) { toastMessage('This marina bills in DockMaster (Settings › Features › Billing system).', 'red', 5000); return false; }
  if (!canBill) { toastMessage('Invoices are for admin and office staff.', 'red'); return false; }
  return true;
}

/* ============ helpers ============ */

const fmtDate = ymd => {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const daysAgoYmd = n => {
  const d = new Date(Date.now() - n * 86400000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const isOverdue = inv => (inv.status === 'sent' || inv.status === 'partial') && inv.dueDate && inv.dueDate < todayYmd();
const customerName = c => [c?.First_Name, c?.Last_Name].filter(Boolean).join(' ') || c?.Company || 'Customer';

/** Billed operation → invoice lines (labor and parts as recorded by Bill Operation). */
function linesFromOp(op) {
  const desc = op.Billing_Description || op.Short_Description || op.Description || 'Service';
  const ref = { jobId: op.id, workOrder: op.Work_Order || null, opType: op.Op_Type || null };
  const labor = toCents(op.Labor_Billed);
  const parts = toCents(op.Parts_Billed);
  const out = [];
  if (labor || !parts) out.push({ kind: 'op', description: parts ? `${desc} (labor)` : desc, qty: 1, unitCents: labor, taxable: true, feeable: true, ref: { ...ref, part: 'labor' } });
  if (parts) out.push({ kind: 'op', description: `${desc} (parts)`, qty: 1, unitCents: parts, taxable: true, feeable: true, ref: { ...ref, part: 'parts' } });
  return out;
}

async function allOps() {
  return (await idbCursorAll('jobData')).filter(j => j && j.id && j.id !== 'lastUpdated');
}
const isBilled = op => !!op.Billing_Completed && op.Status === 'Closed';

/**
 * Operations a storage contract already charges for: "Review & create WOs" turns each selected
 * contract service into an operation on the contract's work orders (contract.woRefs). Priced
 * services are on the contract's balance invoice, so those operations must not be invoiced again.
 * T&M services and anything else added to those work orders are billed normally.
 * → Set of "<workOrder>|<service name>".
 */
async function contractCoveredKeys() {
  const covered = new Set();
  const customers = await idbCursorAll('customerData');
  customers.forEach(cu => Object.values(cu?.Boats || {}).forEach(boat => {
    const c = boat?.Storage_Contract;
    if (!c || !Array.isArray(c.woRefs)) return;
    STORAGE_SERVICE_CATALOG.forEach(e => {
      if (e.unit === 'tm' || !c.selections?.[e.service]) return;
      c.woRefs.forEach(wo => covered.add(`${wo}|${e.service}`));
    });
  }));
  return covered;
}
const coveredByContract = (op, covered) => covered.has(`${op.Work_Order}|${op.Description}`) || covered.has(`${op.Work_Order}|${op.Short_Description}`);

/** A customer's statement, from outside the Invoices window (the customer page). */
export function showStatement(customerId) {
  if (!guard()) return;
  openStatement(customerId);
}

/* ============ list overlay ============ */

export function openInvoicesList(tab = 'invoices', search) {
  if (!guard()) return;
  watch();
  S.tab = tab;
  if (search != null) S.search = String(search);
  if (!S.since) S.since = daysAgoYmd(QUEUE_DAYS);
  document.getElementById('inv-list-overlay')?.remove();
  const wrap = document.createElement('div');
  wrap.id = 'inv-list-overlay';
  wrap.className = 'inv-root inv-backdrop';
  wrap.innerHTML = `
    <div class="inv-panel inv-list-panel" role="dialog" aria-modal="true" aria-labelledby="inv-list-title">
      <div class="inv-head">
        <div class="inv-title" id="inv-list-title">Invoices</div>
        <button type="button" class="inv-btn inv-primary" data-act="new">+ New invoice</button>
        <button type="button" class="inv-x" data-act="close" aria-label="Close">&times;</button>
      </div>
      <div class="inv-tabs" role="tablist">
        <button type="button" role="tab" class="inv-tab" data-tab="invoices">Invoices</button>
        <button type="button" role="tab" class="inv-tab" data-tab="queue">To invoice</button>
        <button type="button" role="tab" class="inv-tab" data-tab="payments">Payments</button>
        <button type="button" role="tab" class="inv-tab" data-tab="accounts">Accounts</button>
      </div>
      <div class="inv-tools" data-for="invoices">
        <div class="inv-seg">${FILTERS.map(([v, l]) => `<button type="button" class="inv-seg-btn" data-filter="${v}">${l}</button>`).join('')}</div>
        <input type="search" class="inv-search" placeholder="Search customer, boat, INV-# or WO" value="${esc(S.search)}">
      </div>
      <div class="inv-tools" data-for="payments">
        <input type="search" class="inv-search inv-pay-search" placeholder="Search customer, RCPT-# or reference" value="${esc(S.search)}">
        <button type="button" class="inv-btn inv-primary inv-small" data-act="record-payment">+ Record payment</button>
      </div>
      <div class="inv-tools" data-for="accounts">
        <input type="search" class="inv-search" placeholder="Search customer or email" value="${esc(S.search)}">
        <span class="inv-muted">What each customer owes, by how late it is.</span>
      </div>
      <div class="inv-tools" data-for="queue">
        <span class="inv-muted">Operations billed since</span>
        <input type="date" class="inv-since" value="${esc(S.since)}">
        <span class="inv-muted">and not on an invoice yet.</span>
      </div>
      <div class="inv-body" id="inv-list"></div>
    </div>`;
  document.body.appendChild(wrap);
  const close = () => { wrap.remove(); if (!document.getElementById('inv-editor')) unwatch(); };
  wrap.addEventListener('click', async e => {
    if (e.target === wrap) return close();
    const t = e.target.closest('[data-act],[data-tab],[data-filter],[data-open],[data-wo],[data-payment]');
    if (!t) return;
    if (t.dataset.act === 'close') return close();
    if (t.dataset.act === 'record-payment') return openPaymentDialog({});
    if (t.dataset.payment) return openPaymentDetail(t.dataset.payment);
    if (t.dataset.act === 'new') return openInvoiceEditor({ draft: { lines: [] } });
    if (t.dataset.tab) { S.tab = t.dataset.tab; return renderList(); }
    if (t.dataset.filter) { S.filter = t.dataset.filter; return renderList(); }
    if (t.dataset.open) return openInvoiceEditor({ invoice: S.invoices[t.dataset.open] });
    if (t.dataset.wo) return invoiceWorkOrder(t.dataset.wo, t.dataset.customer);
  });
  wrap.querySelectorAll('.inv-search').forEach(s => s.addEventListener('input', e => { S.search = e.target.value; renderList(); }));
  wrap.querySelector('.inv-since').addEventListener('change', e => { S.since = e.target.value || daysAgoYmd(QUEUE_DAYS); renderList(); });
  wrap.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
  renderList();
}

async function renderList() {
  const root = document.getElementById('inv-list-overlay');
  if (!root) return;
  root.querySelectorAll('.inv-tab').forEach(b => b.classList.toggle('inv-on', b.dataset.tab === S.tab));
  root.querySelectorAll('.inv-seg-btn').forEach(b => b.classList.toggle('inv-on', b.dataset.filter === S.filter));
  root.querySelectorAll('.inv-tools').forEach(t => { t.hidden = t.dataset.for !== S.tab; });
  const box = document.getElementById('inv-list');
  if (S.tab === 'queue') return renderQueue(box);
  if (S.tab === 'payments') return renderPaymentsList(box, S.search);
  if (S.tab === 'accounts') return renderAccountsTab(box, S.search);

  const q = S.search.trim().toLowerCase();
  const rows = Object.values(S.invoices)
    .filter(inv => inv && inv.id)
    .filter(inv => S.filter === 'all'
      || (S.filter === 'open' ? (inv.status === 'sent' || inv.status === 'partial') : inv.status === S.filter))
    .filter(inv => !q || [inv.number, inv.customerName, inv.boatName, ...(inv.lines || []).map(l => l.ref?.workOrder)]
      .some(v => String(v || '').toLowerCase().includes(q)))
    .sort((a, b) => (b.issuedAt || b.updatedAt || 0) - (a.issuedAt || a.updatedAt || 0));
  if (!rows.length) {
    box.innerHTML = `<div class="inv-empty">${S.filter === 'open' ? 'No open invoices.' : 'Nothing here.'} ${Object.keys(S.invoices).length ? '' : 'Start from <b>To invoice</b> (billed work) or <b>+ New invoice</b>.'}</div>`;
    return;
  }
  box.innerHTML = rows.map(inv => {
    const st = STATUS[inv.status] || STATUS.draft;
    const overdue = isOverdue(inv);
    return `<button type="button" class="inv-row" data-open="${esc(inv.id)}">
      <span class="inv-row-num">${esc(inv.number || 'Draft')}</span>
      <span class="inv-row-main">
        <span class="inv-row-name">${esc(inv.customerName)}${inv.boatName ? ` · ${esc(inv.boatName)}` : ''}${inv.source === 'finance' ? ' <span class="inv-chip">Finance charge</span>' : ''}</span>
        <span class="inv-row-sub">${inv.issuedDate ? `Issued ${esc(fmtDate(inv.issuedDate))}` : `Saved ${esc(new Date(inv.updatedAt || Date.now()).toLocaleDateString())}`}${inv.dueDate && inv.status !== 'void' ? ` · due ${esc(fmtDate(inv.dueDate))}` : ''}${inv.status === 'sent' || inv.status === 'partial' ? (inv.lastEmailedAt ? ' · emailed' : ' · <span class="inv-warn">not emailed</span>') : ''}${inv.lastReminderAt && (inv.status === 'sent' || inv.status === 'partial') ? ` · reminded ${esc(new Date(inv.lastReminderAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }))}` : ''}</span>
      </span>
      <span class="inv-pill ${overdue ? 'inv-pill-overdue' : st.cls}">${overdue ? 'Overdue' : st.label}</span>
      <span class="inv-row-total">${formatMoney(inv.status === 'sent' || inv.status === 'partial' ? inv.balanceCents : inv.totalCents)}</span>
    </button>`;
  }).join('');
}

async function renderQueue(box) {
  box.innerHTML = '<div class="inv-empty">Loading…</div>';
  const [ops, covered] = await Promise.all([allOps(), contractCoveredKeys()]);
  const recent = ops.filter(op => isBilled(op) && !S.index[op.id] && String(op.Billing_Completed).slice(0, 10) >= S.since);
  const billed = recent.filter(op => !coveredByContract(op, covered));
  const viaContract = recent.length - billed.length;
  const waiting = ops.filter(op => op.Status === 'Ready to Bill' || op.Status === 'Complete');
  const byWo = new Map();
  billed.forEach(op => {
    const k = `${op.Customer_ID}|${op.Work_Order}`;
    if (!byWo.has(k)) byWo.set(k, []);
    byWo.get(k).push(op);
  });
  const groups = [...byWo.values()].sort((a, b) => String(b[0].Billing_Completed).localeCompare(String(a[0].Billing_Completed)));
  box.innerHTML = `
    ${groups.length ? groups.map(g => {
      const total = sumCents(g.flatMap(linesFromOp).map(l => l.unitCents));
      const missing = g.some(op => !toCents(op.Labor_Billed) && !toCents(op.Parts_Billed));
      return `<div class="inv-q-row">
        <span class="inv-row-num">${esc(woLabel(g[0].Work_Order))}</span>
        <span class="inv-row-main">
          <span class="inv-row-name">${esc(g[0].Customer_Name || 'Customer')}${g[0].Boat_Name ? ` · ${esc(g[0].Boat_Name)}` : ''}</span>
          <span class="inv-row-sub">${g.length} billed operation${g.length === 1 ? '' : 's'} · last billed ${esc(fmtDate(g[0].Billing_Completed))}${missing ? ' · <span class="inv-warn">no billed amount on some — enter prices on the invoice</span>' : ''}</span>
        </span>
        <span class="inv-row-total">${formatMoney(total)}</span>
        <button type="button" class="inv-btn inv-primary inv-small" data-wo="${esc(g[0].Work_Order)}" data-customer="${esc(g[0].Customer_ID)}">Create invoice</button>
      </div>`;
    }).join('') : `<div class="inv-empty">No billed work waiting for an invoice since ${esc(fmtDate(S.since))}.</div>`}
    ${viaContract ? `<div class="inv-note">${viaContract} billed operation${viaContract === 1 ? ' is a storage-contract service' : 's are storage-contract services'}, charged on the contract's balance invoice instead (Storage Contracts › Invoice balance).</div>` : ''}
    ${waiting.length ? `<div class="inv-note">${waiting.length} operation${waiting.length === 1 ? ' is' : 's are'} Complete or Ready to Bill but not billed yet. Open them in Work Orders and use <b>Bill Operation</b> first; they then show up here.</div>` : ''}`;
}

/* ============ from a work order ============ */

/** Draft invoice from a work order's billed operations that aren't on an invoice yet. */
export async function invoiceWorkOrder(workOrder, customerId) {
  if (!guard()) return;
  watch();
  const index = (await get(ref(database, 'invoiceIndex/jobs'))).val() || {};
  const covered = await contractCoveredKeys();
  const ops = (await allOps()).filter(op => String(op.Work_Order) === String(workOrder) && (!customerId || op.Customer_ID === customerId));
  const billed = ops.filter(op => isBilled(op) && !index[op.id] && !coveredByContract(op, covered));
  if (!billed.length && ops.some(op => isBilled(op) && coveredByContract(op, covered))) {
    toastMessage(`${woLabel(workOrder)}'s billed services are on its storage contract. Invoice them from Storage Contracts › Invoice balance.`, 'red', 6000);
    return;
  }
  if (!billed.length) {
    const unbilled = ops.filter(op => !isBilled(op) && op.Status !== 'Declined').length;
    toastMessage(ops.some(op => index[op.id])
      ? `Everything billed on ${woLabel(workOrder)} is already on an invoice.`
      : `Nothing billed on ${woLabel(workOrder)} yet${unbilled ? `: ${unbilled} operation${unbilled === 1 ? '' : 's'} still to bill (Bill Operation)` : ''}.`, 'red', 5000);
    return;
  }
  const first = billed[0];
  openInvoiceEditor({ draft: { customerId: first.Customer_ID, boatId: first.Boat_ID || null, lines: billed.flatMap(linesFromOp) } });
}

/* ============ from a storage contract ============ */

/** The key functions/invoices.js uses for a contract payment part in /invoiceIndex/contracts. */
export const contractIndexKey = (customerId, boatId, season, part) =>
  `${customerId}_${boatId}_${String(season).replace(/[^A-Za-z0-9-]/g, '-')}_${part}`;

/** The printed contract's number (storageContracts.js uses the same rule). */
const contractNumber = boatId => `SC-${new Date().getFullYear()}-${String(boatId || '').slice(-4).toUpperCase().padStart(4, '0')}`;

/**
 * Draft invoice for one payment of a storage contract.
 *   deposit  the flat deposit due at signing (Settings › Storage rates), untaxed: a prepayment.
 *   balance  the whole agreement as signed (storage taxed, services not, environmental fee on both,
 *            at the storage rates), less the deposit if it was invoiced. Due before haul week.
 * row: { customerId, boatId, boat, customer, contract } as the Storage Contracts list has it.
 */
export async function invoiceStorageContract(row, part) {
  if (!guard()) return;
  watch();
  const settings = { ...STORAGE_DEFAULTS, ...STORAGE_RATE_DEFAULTS, ...((await get(ref(database, 'pricing/storageContract'))).val() || {}) };
  const c = row.contract || {};
  const season = c.season || settings.season || currentSeasonLabel();
  const boatName = row.boat?.Boat_Name || 'Boat';
  const sc = contractNumber(row.boatId);
  const ref0 = p => ({ boatId: row.boatId, season, part: p });
  const taxRate = (Number(settings.taxPct) || 0) / 100;
  const feeRate = (Number(settings.envPct) || 0) / 100;
  const depositCents = toCents(settings.deposit ?? 400);
  const contracts = (await get(ref(database, 'invoiceIndex/contracts'))).val() || {};

  if (part === 'deposit') {
    return openInvoiceEditor({ draft: {
      customerId: row.customerId, boatId: row.boatId, rateSource: 'storage', taxRate, feeRate,
      notes: `Non-refundable deposit for the ${season} winter storage agreement (${sc}), due at signing. It is credited on the balance invoice.`,
      lines: [{ kind: 'contract', description: `Deposit: ${season} winter storage, ${boatName} (${sc})`, qty: 1, unitCents: depositCents, taxable: false, feeable: false, ref: ref0('deposit') }],
    } });
  }

  const totals = c.totals || {};
  const storageCents = toCents(totals.storage);
  const servicesCents = toCents(totals.services);
  if (!storageCents && !servicesCents) {
    toastMessage('This contract has no saved totals. Open it in the builder and Save draft first.', 'red', 6000);
    return;
  }
  const lines = [{ kind: 'contract', description: `${season} winter storage, ${boatName} (${sc})`, qty: 1, unitCents: storageCents, taxable: true, feeable: true, ref: ref0('balance') }];
  // Each selected service at its catalog price, if those still add up to what was signed;
  // otherwise one line for the signed services total.
  const length = Number(row.boat?.Boat_Length) || 0;
  const chosen = STORAGE_SERVICE_CATALOG.filter(e => c.selections?.[e.service] && e.unit !== 'tm')
    .map(e => ({ name: e.service, cents: toCents(e.unit === 'perFoot' ? e.amount * length : e.amount) }));
  if (servicesCents) {
    if (chosen.length && sumCents(chosen.map(x => x.cents)) === servicesCents) {
      chosen.forEach(x => lines.push({ kind: 'contract', description: x.name, qty: 1, unitCents: x.cents, taxable: false, feeable: true, ref: ref0('balance') }));
    } else {
      lines.push({ kind: 'contract', description: `Selected services${chosen.length ? `: ${chosen.map(x => x.name).join(', ')}` : ''}`, qty: 1, unitCents: servicesCents, taxable: false, feeable: true, ref: ref0('balance') });
    }
  }
  const depositInvoiceId = contracts[contractIndexKey(row.customerId, row.boatId, season, 'deposit')];
  const depositInv = depositInvoiceId ? S.invoices[depositInvoiceId] || (await get(ref(database, `invoices/${depositInvoiceId}`))).val() : null;
  const credited = depositInv && depositInv.status !== 'void' && depositInv.status !== 'draft' ? depositInv.totalCents : 0;
  if (credited) {
    lines.push({ kind: 'contract', description: `Less deposit (${depositInv.number})`, qty: 1, unitCents: -credited, taxable: false, feeable: false, ref: ref0('balance') });
  }
  const t = priceTotals({ subtotal: storageCents + servicesCents, taxBase: storageCents, feeBase: storageCents + servicesCents, taxRate, feeRate });
  const signedTotal = toCents(totals.total);
  if (signedTotal && signedTotal !== t.total) {
    toastMessage(`Heads up: this comes to ${formatMoney(t.total)} at today's storage rates; the agreement said ${formatMoney(signedTotal)}. Check Settings › Storage rates or adjust a line.`, 'red', 9000);
  }
  const balance = t.total - credited;
  const half = Math.round(balance / 2);
  openInvoiceEditor({ draft: {
    customerId: row.customerId, boatId: row.boatId, rateSource: 'storage', taxRate, feeRate,
    dueDate: c.haulWeek && /^\d{4}-\d{2}-\d{2}$/.test(c.haulWeek) ? c.haulWeek : null,
    notes: `${season} winter storage agreement ${sc}. Payment schedule: ${formatMoney(half)} (50% of the balance) before haul week, and the remaining ${formatMoney(balance - half)} before launch.${credited ? '' : ' No deposit invoice was found for this agreement, so no deposit is deducted.'}`,
    lines,
  } });
}

/* ============ printable invoice (PDF / email) ============ */

/**
 * Show the printable invoice. The invoice windows sit above the document layer, so they're
 * hidden while it's open; closing the document brings back the list and this invoice.
 */
async function showDocument(inv, { email = false } = {}) {
  const doc = document.getElementById('invoiceContainer1');
  if (!doc) return;
  const open = inv.status === 'sent' || inv.status === 'partial';
  const [terms, pay] = await Promise.all([
    get(ref(database, 'billingTerms')).then(s => s.val()).catch(() => null),
    // The invoice's Pay online link (functions/payLinks.js), when the marina takes payments online.
    open ? call('getPayLink', { invoiceId: inv.id }).catch(err => { console.warn('[invoices] pay link', err); return null; }) : null,
  ]);
  const list = document.getElementById('inv-list-overlay');
  if (list) list.style.display = 'none';
  closeEditor(true);
  openAppInvoiceDocument(inv, {
    termsExtra: inv.source === 'finance' ? '' : termsLine(terms),
    payUrl: pay?.url || null,
    onEmailed: async res => {
      try {
        const updated = await call('markInvoiceEmailed', { id: inv.id, to: res?.to || '' });
        Object.assign(inv, updated);
        changed();
      } catch (err) { console.warn('[invoices] could not note the email on the invoice', err); }
    },
  });
  const obs = new MutationObserver(() => {
    if (!doc.classList.contains('hidden')) return;
    obs.disconnect();
    if (list?.isConnected) list.style.display = '';
    openInvoiceEditor({ invoice: S.invoices[inv.id] || inv });
  });
  obs.observe(doc, { attributes: true, attributeFilter: ['class'] });
  if (email) setTimeout(() => document.getElementById('invDocEmailButton')?.click(), 50);
}

/* ============ editor ============ */

let E = null; // the invoice being edited: { inv, lines, customer, boats, readOnly }

export async function openInvoiceEditor({ invoice, draft }) {
  if (!guard()) return;
  watch();
  const inv = invoice ? JSON.parse(JSON.stringify(invoice)) : { status: 'draft', ...(draft || {}) };
  let customer = null;
  if (inv.customerId) customer = (await get(ref(database, `customers/${inv.customerId}`))).val();
  E = {
    inv,
    lines: (inv.lines || []).map((l, i) => ({ ...l, id: l.id || `L${i + 1}`, taxable: l.taxable !== false, feeable: l.feeable !== false })),
    customer,
    readOnly: inv.status && inv.status !== 'draft',
    taxRate: inv.taxRate ?? S.rates.salesTaxRate,
    feeRate: inv.feeRate ?? S.rates.environmentalFeeRate,
    dirty: false,
    saving: false,
    credits: [],
  };
  if (E.readOnly) E.credits = await creditsFor(inv);
  document.getElementById('inv-editor')?.remove();
  const wrap = document.createElement('div');
  wrap.id = 'inv-editor';
  wrap.className = 'inv-root inv-backdrop';
  document.body.appendChild(wrap);
  wrap.addEventListener('click', onEditorClick);
  wrap.addEventListener('input', onEditorInput);
  wrap.addEventListener('change', onEditorInput);
  wrap.addEventListener('keydown', e => { if (e.key === 'Escape') closeEditor(); });
  renderEditor();
}

/** Credit on the customer's account (overpayments, paid deposits) that could pay this invoice, oldest first. */
async function creditsFor(inv) {
  if (!(inv.status === 'sent' || inv.status === 'partial') || !inv.customerId) return [];
  try {
    const snap = await get(query(ref(database, 'payments'), orderByChild('customerId'), equalTo(inv.customerId)));
    return Object.values(snap.val() || {})
      .filter(p => p && p.status === 'ok' && p.unappliedCents > 0)
      .sort((a, b) => String(a.receivedDate).localeCompare(String(b.receivedDate)))
      .map(p => ({ id: p.id, number: p.number, unappliedCents: p.unappliedCents, note: p.note || '' }));
  } catch (err) { console.warn('[invoices] customer credit', err); return []; }
}

function closeEditor(force) {
  if (!force && E?.dirty && !confirm('Close without saving your changes?')) return;
  document.getElementById('inv-editor')?.remove();
  E = null;
  if (!document.getElementById('inv-list-overlay')) unwatch();
}

function clientTotals() {
  const lines = E.lines.map(l => ({ ...l, amountCents: lineCents(l.qty, l.unitCents) }));
  const t = priceTotals({
    subtotal: sumCents(lines.map(l => l.amountCents)),
    taxBase: sumCents(lines.filter(l => l.taxable).map(l => l.amountCents)),
    feeBase: sumCents(lines.filter(l => l.feeable).map(l => l.amountCents)),
    taxRate: E.taxRate, feeRate: E.feeRate,
  });
  return { lines, ...t };
}

function renderEditor() {
  const wrap = document.getElementById('inv-editor');
  if (!wrap || !E) return;
  const { inv, readOnly } = E;
  const st = STATUS[inv.status] || STATUS.draft;
  const t = clientTotals();
  const pct = r => `${Number((Number(r || 0) * 100).toFixed(3))}%`;
  const boats = E.customer?.Boats ? Object.entries(E.customer.Boats) : [];
  const overdue = isOverdue(inv);
  wrap.innerHTML = `
    <div class="inv-panel inv-editor-panel" role="dialog" aria-modal="true" aria-labelledby="inv-ed-title">
      <div class="inv-head">
        <div class="inv-title" id="inv-ed-title">${esc(inv.number || 'New invoice')}</div>
        <span class="inv-pill ${overdue ? 'inv-pill-overdue' : st.cls}">${overdue ? 'Overdue' : st.label}</span>
        <button type="button" class="inv-x" data-act="close" aria-label="Close">&times;</button>
      </div>
      <div class="inv-body inv-ed-body">
        <div class="inv-ed-who">
          ${E.customer ? `
            <div class="inv-field"><span>Customer</span><div class="inv-who-name">${esc(customerName(E.customer))}${!readOnly && !inv.id ? ' <button type="button" class="inv-link" data-act="change-customer">Change</button>' : ''}</div>
              <div class="inv-muted">${esc(E.customer.Email || 'No email on file')}</div></div>
            <label class="inv-field"><span>Boat</span>
              <select data-k="boat"${readOnly ? ' disabled' : ''}>
                <option value="">— none —</option>
                ${boats.map(([id, b]) => `<option value="${esc(id)}"${id === inv.boatId ? ' selected' : ''}>${esc(b.Boat_Name || 'Boat')}</option>`).join('')}
              </select></label>` : `
            <label class="inv-field inv-grow"><span>Customer</span>
              <input type="search" data-k="cust-search" placeholder="Type a name, boat or email" autocomplete="off"></label>
            <div class="inv-cust-results" id="inv-cust-results"></div>`}
        </div>

        <table class="inv-lines">
          <thead><tr><th>Description</th><th class="inv-num">Qty</th><th class="inv-num">Price</th><th class="inv-c">Tax</th><th class="inv-num">Amount</th>${readOnly ? '' : '<th></th>'}</tr></thead>
          <tbody>
            ${t.lines.map((l, i) => `
              <tr data-i="${i}">
                <td>${l.ref?.workOrder ? `<span class="inv-chip">${esc(woLabel(l.ref.workOrder))}</span>` : ''}
                  ${readOnly ? esc(l.description) : `<input class="inv-in" data-k="description" value="${esc(l.description)}" maxlength="300">`}</td>
                <td class="inv-num">${readOnly ? esc(l.qty) : `<input class="inv-in inv-in-num" data-k="qty" inputmode="decimal" value="${esc(l.qty)}">`}</td>
                <td class="inv-num">${readOnly ? formatMoney(l.unitCents) : `<input class="inv-in inv-in-num${l.unitCents === 0 ? ' inv-in-warn' : ''}" data-k="price" inputmode="decimal" value="${esc(toDollars(l.unitCents).toFixed(2))}">`}</td>
                <td class="inv-c"><input type="checkbox" data-k="taxable"${l.taxable ? ' checked' : ''}${readOnly ? ' disabled' : ''} aria-label="Taxable"></td>
                <td class="inv-num" data-amount>${formatMoney(l.amountCents)}</td>
                ${readOnly ? '' : `<td><button type="button" class="inv-x inv-x-small" data-act="remove" aria-label="Remove line">&times;</button></td>`}
              </tr>`).join('') || `<tr><td colspan="6" class="inv-muted">No lines yet.</td></tr>`}
          </tbody>
        </table>
        ${readOnly ? '' : '<button type="button" class="inv-link" data-act="add-line">+ Add a line</button>'}

        <div class="inv-ed-bottom">
          <label class="inv-field inv-grow"><span>Notes on the invoice</span>
            <textarea data-k="notes" rows="3"${readOnly ? ' disabled' : ''}>${esc(inv.notes || '')}</textarea></label>
          <div class="inv-totals">
            <div><span>Subtotal</span><span data-t="subtotal">${formatMoney(t.subtotal)}</span></div>
            <div><span>Environmental fee · ${pct(E.feeRate)}</span><span data-t="fee">${formatMoney(t.fee)}</span></div>
            <div><span>Sales tax · ${pct(E.taxRate)}</span><span data-t="tax">${formatMoney(t.tax)}</span></div>
            <div class="inv-total"><span>Total</span><span data-t="total">${formatMoney(t.total)}</span></div>
            ${readOnly && inv.status !== 'void' ? `<div><span>Paid</span><span>${formatMoney(inv.paidCents || 0)}</span></div>
              <div class="inv-total"><span>Balance due</span><span>${formatMoney(inv.balanceCents)}</span></div>` : ''}
          </div>
        </div>
        ${readOnly ? `<div class="inv-muted inv-meta">
          ${inv.issuedDate ? `Issued ${esc(fmtDate(inv.issuedDate))}` : ''}${inv.dueDate ? ` · due ${esc(fmtDate(inv.dueDate))}` : ''}
          ${inv.lastEmailedAt ? `<br>Emailed ${esc(new Date(inv.lastEmailedAt).toLocaleString())}${inv.lastEmailedTo ? ` to ${esc(inv.lastEmailedTo)}` : ''}${inv.emailCount > 1 ? ` (${inv.emailCount} times)` : ''}` : (inv.status === 'sent' ? '<br><span class="inv-warn">Not emailed yet.</span>' : '')}
          ${Object.keys(inv.payments || {}).length ? `<br>Payments: <span data-k="pay-list">${Object.entries(inv.payments).map(([pid, c]) => `<button type="button" class="inv-link" data-pay="${esc(pid)}">${formatMoney(c)}</button>`).join(', ')}</span>` : ''}
          ${inv.status === 'void' ? `<br>Voided ${esc(new Date(inv.voidedAt).toLocaleDateString())} by ${esc(inv.voidedBy || '')}: ${esc(inv.voidReason || '')}` : ''}</div>` : ''}
      </div>
      <div class="inv-foot">
        ${inv.status === 'draft' && inv.id ? '<button type="button" class="inv-btn inv-danger-ghost" data-act="delete">Delete draft</button>' : ''}
        ${readOnly && inv.status !== 'void' ? '<button type="button" class="inv-btn inv-danger-ghost" data-act="void">Void…</button>' : ''}
        <span class="inv-spacer"></span>
        ${inv.id ? '<button type="button" class="inv-btn" data-act="pdf">Print / PDF</button>' : ''}
        ${readOnly && inv.status !== 'void' ? '<button type="button" class="inv-btn" data-act="email">Email to customer</button>' : ''}
        ${(inv.status === 'sent' || inv.status === 'partial') && E.credits?.length ? `<button type="button" class="inv-btn" data-act="apply-credit" title="${esc(E.credits.map(c => `${c.number}: ${formatMoney(c.unappliedCents)}${c.note ? ` (${c.note})` : ''}`).join(' · '))}">Apply credit (${formatMoney(Math.min(inv.balanceCents, E.credits.reduce((t, c) => t + c.unappliedCents, 0)))})</button>` : ''}
        ${inv.status === 'sent' || inv.status === 'partial' ? '<button type="button" class="inv-btn" data-act="paylink">Copy pay link</button>' : ''}
        ${inv.status === 'sent' || inv.status === 'partial' ? '<button type="button" class="inv-btn inv-primary" data-act="pay">Record payment</button>' : ''}
        <button type="button" class="inv-btn" data-act="close">${readOnly ? 'Close' : 'Cancel'}</button>
        ${readOnly ? '' : `<button type="button" class="inv-btn" data-act="save"${E.saving ? ' disabled' : ''}>Save draft</button>
          <button type="button" class="inv-btn inv-primary" data-act="issue"${E.saving ? ' disabled' : ''}>Issue invoice</button>`}
      </div>
    </div>`;
}

function onEditorInput(e) {
  if (!E) return;
  const k = e.target.dataset.k;
  if (!k) return;
  if (k === 'cust-search') return searchCustomers(e.target.value);
  if (k === 'boat') { E.inv.boatId = e.target.value || null; E.dirty = true; return; }
  if (k === 'notes') { E.inv.notes = e.target.value; E.dirty = true; return; }
  const i = Number(e.target.closest('tr')?.dataset.i);
  const line = E.lines[i];
  if (!line) return;
  E.dirty = true;
  if (k === 'description') line.description = e.target.value;
  if (k === 'taxable') line.taxable = e.target.checked;
  if (k === 'qty') line.qty = Number(e.target.value) || 0;
  if (k === 'price') {
    line.unitCents = toCents(e.target.value);
    e.target.classList.toggle('inv-in-warn', !line.unitCents);
  }
  if (k === 'qty' || k === 'price' || k === 'taxable') refreshAmounts();
}

/** Update each line's amount and the totals in place (rebuilding would lose the caret and focus). */
function refreshAmounts() {
  const wrap = document.getElementById('inv-editor');
  if (!wrap || !E) return;
  const t = clientTotals();
  wrap.querySelectorAll('.inv-lines tr[data-i]').forEach(tr => {
    const l = t.lines[Number(tr.dataset.i)];
    const cell = tr.querySelector('[data-amount]');
    if (l && cell) cell.textContent = formatMoney(l.amountCents);
  });
  [['subtotal', t.subtotal], ['fee', t.fee], ['tax', t.tax], ['total', t.total]].forEach(([k, v]) => {
    const el = wrap.querySelector(`[data-t="${k}"]`);
    if (el) el.textContent = formatMoney(v);
  });
}

let custCache = null;
async function searchCustomers(text) {
  const box = document.getElementById('inv-cust-results');
  if (!box) return;
  const q = text.trim().toLowerCase();
  if (q.length < 2) { box.innerHTML = ''; return; }
  custCache ||= (await idbCursorAll('customerData')).filter(c => c && c.id && c.id !== 'lastUpdated');
  const hits = custCache.filter(c => [customerName(c), c.Email, c.Phone, ...Object.values(c.Boats || {}).map(b => b?.Boat_Name)]
    .some(v => String(v || '').toLowerCase().includes(q))).slice(0, 8);
  box.innerHTML = hits.map(c => `<button type="button" class="inv-cust-hit" data-cust="${esc(c.id)}">
      <b>${esc(customerName(c))}</b> <span class="inv-muted">${esc(Object.values(c.Boats || {}).map(b => b?.Boat_Name).filter(Boolean).join(', '))}</span></button>`).join('')
    || '<div class="inv-muted">No customer matches.</div>';
}

async function onEditorClick(e) {
  if (!E) return;
  const wrap = document.getElementById('inv-editor');
  if (e.target === wrap) return closeEditor();
  const hit = e.target.closest('[data-cust]');
  if (hit) {
    E.inv.customerId = hit.dataset.cust;
    E.customer = (await get(ref(database, `customers/${hit.dataset.cust}`))).val();
    const boats = Object.keys(E.customer?.Boats || {});
    E.inv.boatId = boats.length === 1 ? boats[0] : null;
    E.dirty = true;
    return renderEditor();
  }
  const payLink = e.target.closest('[data-pay]')?.dataset.pay;
  if (payLink) { closeEditor(true); return openPaymentDetail(payLink); }
  const act = e.target.closest('[data-act]')?.dataset.act;
  if (!act) return;
  if (act === 'close') return closeEditor();
  if (act === 'change-customer') { E.customer = null; E.inv.customerId = null; E.inv.boatId = null; return renderEditor(); }
  if (act === 'add-line') {
    E.lines.push({ id: `L${Date.now()}`, kind: 'manual', description: '', qty: 1, unitCents: 0, taxable: true, feeable: true, ref: null });
    E.dirty = true;
    renderEditor();
    const rows = document.querySelectorAll('#inv-editor .inv-lines [data-k="description"]');
    rows[rows.length - 1]?.focus();
    return;
  }
  if (act === 'remove') {
    E.lines.splice(Number(e.target.closest('tr').dataset.i), 1);
    E.dirty = true;
    return renderEditor();
  }
  if (act === 'pay') {
    const inv = E.inv;
    closeEditor(true);
    return openPaymentDialog({ customerId: inv.customerId, invoiceId: inv.id });
  }
  if (act === 'apply-credit') {
    // Oldest credit first, up to what's owed (functions/payments.js applyCredit, one per payment).
    let left = E.inv.balanceCents;
    let applied = 0;
    try {
      for (const c of E.credits) {
        if (left <= 0) break;
        const take = Math.min(left, c.unappliedCents);
        await call('applyCredit', { paymentId: c.id, invoiceId: E.inv.id, amountCents: take });
        left -= take;
        applied += take;
      }
      toastMessage(`${formatMoney(applied)} credit applied to ${E.inv.number}`, 'green');
    } catch (err) { toastMessage(String(err.message || err), 'red', 7000); }
    changed();
    const fresh = (await get(ref(database, `invoices/${E.inv.id}`))).val();
    if (fresh) openInvoiceEditor({ invoice: fresh });
    return;
  }
  if (act === 'paylink') {
    // The invoice's Pay online address (functions/payLinks.js), for a text or a phone call.
    try {
      const r = await call('getPayLink', { invoiceId: E.inv.id });
      if (!r.url) { toastMessage(r.reason || 'Online payment isn’t set up.', 'red', 6000); return; }
      await navigator.clipboard.writeText(r.url).catch(() => window.prompt('Pay link (copy it):', r.url));
      toastMessage('Pay link copied', 'green');
    } catch (err) { toastMessage(String(err.message || err), 'red', 6000); }
    return;
  }
  if (act === 'pdf' || act === 'email') {
    if (E.dirty) { toastMessage('Save the draft first', 'red'); return; }
    return showDocument(E.inv, { email: act === 'email' });
  }
  if (act === 'save') return save(false);
  if (act === 'issue') return save(true);
  if (act === 'delete') {
    if (!confirm('Delete this draft? Its operations go back to the To invoice list.')) return;
    try {
      await call('deleteDraftInvoice', { id: E.inv.id });
      changed();
      toastMessage('Draft deleted', 'green');
      closeEditor(true);
    } catch (err) { toastMessage(errText(err), 'red', 6000); }
    return;
  }
  if (act === 'void') {
    const reason = prompt(`Void ${E.inv.number}? It keeps its number and its operations go back to the To invoice list.\n\nReason:`);
    if (!reason || !reason.trim()) return;
    try {
      E.inv = await call('voidInvoice', { id: E.inv.id, reason: reason.trim() });
      changed();
      toastMessage(`${E.inv.number} voided`, 'green');
      renderEditor();
    } catch (err) { toastMessage(errText(err), 'red', 6000); }
  }
}

/** Pull any typed-but-not-yet-left qty/price values into E.lines before saving. */
function syncInputs() {
  document.querySelectorAll('#inv-editor .inv-lines tr[data-i]').forEach(tr => {
    const line = E.lines[Number(tr.dataset.i)];
    if (!line) return;
    const q = tr.querySelector('[data-k="qty"]');
    const p = tr.querySelector('[data-k="price"]');
    const d = tr.querySelector('[data-k="description"]');
    if (q) line.qty = Number(q.value) || 0;
    if (p) line.unitCents = toCents(p.value);
    if (d) line.description = d.value;
  });
}

async function save(issue) {
  if (!E || E.saving) return;
  syncInputs();
  if (!E.inv.customerId) { toastMessage('Choose a customer first', 'red'); return; }
  const blank = E.lines.findIndex(l => !String(l.description || '').trim());
  if (blank >= 0) { toastMessage(`Line ${blank + 1} needs a description`, 'red'); return; }
  if (!E.lines.length) { toastMessage('Add at least one line', 'red'); return; }
  if (issue) {
    const zero = E.lines.filter(l => !l.unitCents).length;
    const total = clientTotals().total;
    if (!confirm(`Issue this invoice for ${formatMoney(total)}?${zero ? `\n\n${zero} line${zero === 1 ? ' has' : 's have'} no price.` : ''}\n\nIt gets its invoice number and can't be edited after this (you can void it).`)) return;
  }
  E.saving = true;
  renderEditor();
  try {
    const saved = await call('saveInvoice', {
      id: E.inv.id || null, customerId: E.inv.customerId, boatId: E.inv.boatId || null,
      notes: E.inv.notes || '', dueDate: E.inv.dueDate || null, rateSource: E.inv.rateSource || null,
      lines: E.lines.map(l => ({ id: l.id, kind: l.kind, description: l.description, qty: l.qty, unitCents: l.unitCents, taxable: l.taxable, feeable: l.feeable, ref: l.ref || null })),
    });
    E.inv = saved;
    E.lines = saved.lines.map(l => ({ ...l }));
    E.taxRate = saved.taxRate;
    E.feeRate = saved.feeRate;
    E.dirty = false;
    changed();
    if (issue) {
      const issued = await call('issueInvoice', { id: saved.id });
      E.inv = issued;
      E.readOnly = true;
      E.credits = await creditsFor(issued); // a paid deposit can come off straight away
      changed();
      toastMessage(`${issued.number} issued for ${formatMoney(issued.totalCents)}`, 'green', 4000);
    } else {
      toastMessage('Draft saved', 'green');
    }
  } catch (err) {
    toastMessage(errText(err), 'red', 7000);
  } finally {
    if (E) { E.saving = false; renderEditor(); }
  }
}
